import addProductsToWishlistQuery from '~layers/app/graphql/wishlist/addProductsToWishlist.gql'
import removeProductsFromWishlistQuery from '~layers/app/graphql/wishlist/removeProductsFromWishlist.gql'

export type WishlistItem = {
  sku: string
}

export function useWishlist() {
  const latest = useState<WishlistItem | null>('latest', () => null)
  const merging = useState<boolean>('merging', () => false)

  const wishlist = useLocalStorage<WishlistItem[]>('wishlist', [], { initOnMounted: true })

  const { customerData } = useAccount()

  const wishlistId = computed(() => customerData.value?.customer?.wishlist?.id)

  async function removeFromWishlist(sku: string) {
    const index = wishlist.value.findIndex(item => item.sku === sku)
    wishlist.value.splice(index, 1)
    if (wishlistId.value) {
      const itemId = customerData.value.customer.wishlist.items.items
        .find(({ product }: { product: { sku: string } }) => product.sku === sku).id
      const {
        mutate,
      } = useMutation(
        removeProductsFromWishlistQuery,
        { clientId: 'customer' },
      )
      try {
        const result = await mutate(
          {
            wishlistId: wishlistId.value,
            wishlistItemsIds: [itemId],
          },
        )
        customerData.value.customer.wishlist = result?.data.removeProductsFromWishlist.wishlist
      }
      catch (error: any) {
        console.error('removeProductsFromWishlist error', error)
      }
    }
  }

  async function addToWishlist(sku: string) {
    wishlist.value.push({ sku })
    latest.value = { sku }
    if (wishlistId.value) {
      const {
        mutate,
      } = useMutation(
        addProductsToWishlistQuery,
        { clientId: 'customer' },
      )
      try {
        const result = await mutate(
          {
            wishlistId: wishlistId.value,
            wishlistItems: [{ sku, quantity: 1 }],
          },
        )
        customerData.value.customer.wishlist = result?.data.addProductsToWishlist.wishlist
      }
      catch (error: any) {
        console.error('addProductsToWishlist error', error)
      }
    }
  }

  const toggleWishlist = async (sku: string) => {
    if (wishlist.value.find(item => item.sku === sku)) {
      await removeFromWishlist(sku)
    }
    else {
      await addToWishlist(sku)
    }
  }
  const count = computed(() => wishlist.value.length)

  watch(wishlistId, async () => {
    if (!merging.value && wishlistId.value) {
      merging.value = true

      // add products to customer wishlist
      const skusOnCustomerWishlist = customerData.value?.customer?.wishlist.items.items.map(
        (wishlistItem: any) => wishlistItem.product.sku,
      )
      const skusToAddToCustomerWishlist = wishlist.value.filter(({ sku }) => !skusOnCustomerWishlist.includes(sku))
      if (skusToAddToCustomerWishlist.length) {
        const {
          mutate,
        } = useMutation(
          addProductsToWishlistQuery,
          { clientId: 'customer' },
        )
        try {
          const result = await mutate(
            {
              wishlistId: wishlistId.value,
              wishlistItems: skusToAddToCustomerWishlist.map(
                ({ sku }) => ({ sku, quantity: 1 }),
              ),
            },
          )
          customerData.value.customer.wishlist = result?.data.addProductsToWishlist.wishlist
        }
        catch (error: any) {
          console.error('addProductsToWishlist error', error)
        }
      }

      // update local wishlist
      wishlist.value = customerData.value.customer.wishlist.items.items.map(
        (wishlistItem: any) => ({ sku: wishlistItem.product.sku }),
      )

      merging.value = false
    }
  })

  return {
    count,
    latest,
    toggleWishlist,
    wishlist,
  }
}
